import { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit, X, Check, TrendingUp, TrendingDown } from 'lucide-react';

const API = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8001/api'
  : '/api';

function OpenPositionsManagement() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editValues, setEditValues] = useState({ entry_price: 0, amount: 0 });

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchAllPositions();
  }, []);

  const fetchAllPositions = async () => {
    try {
      // Get all users
      const usersRes = await axios.get(`${API}/admin/users`, axiosConfig);
      
      // Get positions for all users
      const allPositions = [];
      for (const user of usersRes.data) {
        try {
          const posRes = await axios.get(`${API}/admin/user/${user.id}/positions`, axiosConfig);
          posRes.data.forEach(pos => {
            allPositions.push({
              ...pos,
              symbol: pos.asset_symbol || pos.symbol, // Ensure symbol exists
              user_email: user.email,
              user_name: user.full_name
            });
          });
        } catch (err) {
          // Skip if no positions
        }
      }
      
      // Get current prices and calculate profit/loss
      const assetsRes = await axios.get(`${API}/assets`, axiosConfig);
      const assets = assetsRes.data;
      
      allPositions.forEach(pos => {
        const asset = assets.find(a => a.symbol === pos.symbol);
        if (asset) {
          pos.current_price = asset.current_price;
          const entryPrice = pos.avg_buy_price || pos.entry_price;
          pos.profit_loss = (asset.current_price - entryPrice) * pos.amount;
          let pnl_percent = ((asset.current_price - entryPrice) / entryPrice) * 100;
          
          // %10 SINIRI SADECE BIST_100 için geçerli
          const category = asset.category || pos.asset_category || '';
          if (category === 'BIST_100') {
            if (pnl_percent > 10) {
              pnl_percent = 10;
            } else if (pnl_percent < -10) {
              pnl_percent = -10;
            }
          }
          
          pos.profit_loss_percent = pnl_percent;
          
          console.log(`Position ${pos.symbol}:`, {
            entry: entryPrice,
            current: asset.current_price,
            amount: pos.amount,
            pnl: pos.profit_loss,
            pnl_pct: pos.profit_loss_percent
          });
        }
      });
      
      setPositions(allPositions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching positions:', error);
      setLoading(false);
    }
  };

  const handleStartEdit = (position) => {
    setEditingPosition(position.id);
    setEditValues({
      entry_price: position.avg_buy_price || position.entry_price,
      amount: position.amount
    });
  };

  const handleCancelEdit = () => {
    setEditingPosition(null);
    setEditValues({ entry_price: 0, amount: 0 });
  };

  const handleSaveEdit = async (positionId) => {
    try {
      await axios.put(
        `${API}/admin/positions/${positionId}?entry_price=${editValues.entry_price}&amount=${editValues.amount}`,
        {},
        axiosConfig
      );
      
      alert('✅ Pozisyon güncellendi!');
      setEditingPosition(null);
      fetchAllPositions();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.detail || 'Hata oluştu!'));
    }
  };

  if (loading) {
    return <div className="text-white text-center py-12">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-white font-bold text-2xl mb-2">Tüm Açık Pozisyonlar</h2>
        <p className="text-gray-400">Kullanıcıların açık pozisyonlarını görüntüleyin ve düzenleyin</p>
      </div>

      <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="text-blue-400 text-2xl">💡</div>
          <div className="text-blue-300 text-sm">
            <strong>Pozisyon Düzenleme</strong>
            <p className="mt-2 text-gray-300">
              Giriş fiyatı ve hisse miktarını düzenleyebilirsiniz. Kar/Zarar otomatik olarak yeniden hesaplanacaktır.
            </p>
          </div>
        </div>
      </div>

      {positions.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
          <p className="text-gray-400 text-lg">Açık pozisyon bulunmuyor</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-4 text-gray-400 font-medium">Kullanıcı</th>
                <th className="text-left p-4 text-gray-400 font-medium">Varlık</th>
                <th className="text-left p-4 text-gray-400 font-medium">Giriş Fiyatı</th>
                <th className="text-left p-4 text-gray-400 font-medium">Miktar</th>
                <th className="text-left p-4 text-gray-400 font-medium">Kaldıraç</th>
                <th className="text-left p-4 text-gray-400 font-medium">Gizli Kaldıraç</th>
                <th className="text-left p-4 text-gray-400 font-medium">Güncel Fiyat</th>
                <th className="text-left p-4 text-gray-400 font-medium">Kar/Zarar</th>
                <th className="text-right p-4 text-gray-400 font-medium">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {positions.map(position => (
                <tr key={position.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{position.user_name}</p>
                      <p className="text-gray-400 text-sm">{position.user_email}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-white font-medium">{position.symbol}</span>
                  </td>
                  <td className="p-4">
                    {editingPosition === position.id ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editValues.entry_price}
                        onChange={(e) => setEditValues({ ...editValues, entry_price: parseFloat(e.target.value) })}
                        className="w-24 bg-gray-600 text-white px-2 py-1 rounded text-center"
                      />
                    ) : (
                      <span className="text-gray-300">₺{(position.avg_buy_price || position.entry_price).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="p-4">
                    {editingPosition === position.id ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editValues.amount}
                        onChange={(e) => setEditValues({ ...editValues, amount: parseFloat(e.target.value) })}
                        className="w-24 bg-gray-600 text-white px-2 py-1 rounded text-center"
                      />
                    ) : (
                      <span className="text-gray-300">{position.amount}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className="text-blue-400 font-medium">{position.leverage || 1}x</span>
                  </td>
                  <td className="p-4">
                    <span className="text-yellow-400 font-bold">{position.hidden_leverage || 1}x</span>
                  </td>
                  <td className="p-4 text-gray-300">
                    ₺{position.current_price?.toFixed(2) || '0.00'}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      {position.profit_loss >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-400" />
                      )}
                      <span className={position.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}>
                        ₺{position.profit_loss?.toFixed(2) || '0.00'}
                      </span>
                      <span className={`text-sm ${position.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ({position.profit_loss_percent?.toFixed(2) || '0.00'}%)
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    {editingPosition === position.id ? (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleSaveEdit(position.id)}
                          className="text-green-400 hover:text-green-300"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleStartEdit(position)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OpenPositionsManagement;
